"use client"

import { useEffect, useState } from "react"
import { getSuspectFrame, formatFrame, isStackLikelyMapped, parseStack } from "@/lib/crash-locator"
import { persistCrashReport } from "@/components/error-capture"

function getBuildId(): string {
  if (typeof window === "undefined") return "ssr"
  const nextData = (window as unknown as { __NEXT_DATA__?: { buildId?: string } }).__NEXT_DATA__
  return nextData?.buildId ?? "unknown"
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [documentId, setDocumentId] = useState<string | null>(null)

  useEffect(() => {
    const stack = error.stack
    const suspect = getSuspectFrame(stack)
    const suspectFormatted = suspect ? formatFrame(suspect) : null
    const sourcemapWarn = !isStackLikelyMapped(stack)
    const buildId = getBuildId()

    console.error(
      "[CRASH LOCATOR] global-error",
      "\n  message:",
      error.message,
      "\n  buildId:",
      buildId,
      "\n  suspect file:line:col:",
      suspectFormatted ?? "(none)",
      "\n  full stack:\n",
      stack ?? "(no stack)"
    )
    if (sourcemapWarn) {
      console.warn("[CRASH LOCATOR] Sourcemap may not be applied — check .map serving.")
    }

    if (process.env.NODE_ENV === "development") {
      const frames = parseStack(stack).slice(0, 10).map((f) => formatFrame(f))
      persistCrashReport({
        message: error.message,
        stack,
        route: typeof window !== "undefined" ? window.location.pathname : "",
        buildId,
        suspect: suspectFormatted,
        frames,
        userId: null,
      }).then((id) => id && setDocumentId(id))
    }
  }, [error])

  const suspect = getSuspectFrame(error.stack)
  const suspectFormatted = suspect ? formatFrame(suspect) : null

  return (
    <html lang="en">
      <body style={{ fontFamily: "system-ui", padding: "1rem", background: "#1a1a1a", color: "#e0e0e0" }}>
        <h1 style={{ color: "#f87171" }}>Application error</h1>
        <p><strong>Message:</strong> {error.message}</p>
        <p><strong>Suspect (file:line:col):</strong> <code style={{ background: "#333", padding: "2px 6px" }}>{suspectFormatted ?? "(none)"}</code></p>
        {documentId && (
          <p><strong>Firestore doc:</strong> <code style={{ background: "#333", padding: "2px 6px" }}>debug_crashes/{documentId}</code></p>
        )}
        <pre style={{ overflow: "auto", background: "#222", padding: "1rem", fontSize: "12px" }}>
          {error.stack ?? "(no stack)"}
        </pre>
        <button
          type="button"
          onClick={() => reset()}
          style={{ padding: "8px 16px", cursor: "pointer", background: "#3b82f6", color: "white", border: "none", borderRadius: "4px" }}
        >
          Try again
        </button>
      </body>
    </html>
  )
}
